import { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChatBubble } from './ChatBubble';
import { ChatPanel } from './ChatPanel';
import { useChatSettings } from './useChatSettings';
import type { TabId } from './chat.types';

interface ChatWidgetProps {
  hideChrome: boolean;
}

export function ChatWidget({ hideChrome }: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('bot');
  const { settings, loading } = useChatSettings();
  const fbInjected = useRef(false);

  // Inject Meta Chat Plugin site-wide when admin enables it.
  // Runs at the App level (via ChatWidget) so it's independent of whether
  // the user has opened the chat widget.
  useEffect(() => {
    if (hideChrome || loading) return;
    if (!settings.fb_chat_plugin_enabled || !settings.messenger_page_id) return;
    if (fbInjected.current) return;
    fbInjected.current = true;

    if (!document.getElementById('fb-root')) {
      const r = document.createElement('div');
      r.id = 'fb-root';
      document.body.prepend(r);
    }
    if (!document.querySelector('.fb-customerchat')) {
      const d = document.createElement('div');
      d.className = 'fb-customerchat';
      d.setAttribute('attribution', 'biz_inbox');
      d.setAttribute('page_id', settings.messenger_page_id);
      d.setAttribute('theme_color', '#E8A838');
      d.setAttribute('logged_in_greeting', 'Hi! How can we help you today?');
      d.setAttribute('logged_out_greeting', 'Hi! How can we help you today?');
      document.body.appendChild(d);
    }
    if (!document.getElementById('facebook-jssdk')) {
      const s = document.createElement('script');
      s.id = 'facebook-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk/xfbml.customerchat.js';
      s.async = true;
      s.defer = true;
      s.crossOrigin = 'anonymous';
      document.head.appendChild(s);
    }
  }, [hideChrome, loading, settings.fb_chat_plugin_enabled, settings.messenger_page_id]);

  if (hideChrome || loading || !settings.widget_enabled) return null;

  const firstEnabledTab = settings.enabled_tabs[0] ?? 'bot';
  const currentTab = settings.enabled_tabs.includes(activeTab) ? activeTab : firstEnabledTab;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <ChatPanel
            activeTab={currentTab}
            onTabChange={tab => setActiveTab(tab)}
            settings={settings}
          />
        )}
      </AnimatePresence>
      <div className="mt-3">
        <ChatBubble isOpen={isOpen} onClick={() => setIsOpen(o => !o)} />
      </div>
    </div>
  );
}
